import axios from 'axios';
import firebaseConfig from '../firebaseConfig';
import { auth } from '../firebase';
import { getSignedURL, setSignedURLs } from './signedUrlCache';

/**
 * Convert original filename to resized filename with extension suffix
 * @param {string} filename - Original filename (e.g., 'image.jpg')
 * @returns {string} - Resized filename (e.g., 'image_4000x8000.webp')
 */
export const getResizedFilename = (filename) => {
	const baseName = filename.replace(/\.[^/.]+$/, ''); // Remove extension
	return `${baseName}_4000x8000.webp`;
};

export const callFirebaseFunction = async (functionName, body = {}) => {
	const token = await auth.currentUser.getIdToken(true);
	
	// Always use deployed functions (emulators disabled for production testing)
	const functionURL = `https://${firebaseConfig.defaultRegion}-${firebaseConfig.projectId}.cloudfunctions.net/${functionName}`;

	console.log('📞 Calling function:', functionName);
	console.log('🔗 URL:', functionURL);

	try {
		const response = await axios.post(functionURL, body, {
			headers: {
				Authorization: `Bearer ${token}`,
			},
		});
		return response.data;
	} catch (error) {
		console.error('Function call failed:', error.response?.data || error.message);
		throw new Error(error.response ? error.response.data.message : 'Failed to create user');
	}
};

/**
 * Get signed URLs for image keys with caching
 * @param {string} projectId - Project ID
 * @param {string[]} keys - Array of storage keys (e.g., ["projectId/images/file.webp"])
 * @returns {Promise<Object>} - Map of keys to signed URLs
 */
export const getSignedURLs = async (projectId, keys) => {
	if (!keys || keys.length === 0) {
		return {};
	}

	const result = {};
	const missingKeys = [];

	// Check cache first
	for (const key of keys) {
		const cachedUrl = getSignedURL(key);
		if (cachedUrl) {
			result[key] = cachedUrl;
		} else {
			missingKeys.push(key);
		}
	}

	// Fetch missing URLs from backend
	if (missingKeys.length > 0) {
		try {
			const response = await callFirebaseFunction('getImageURLs', {
				projectId,
				keys: missingKeys,
			});

			if (response.urls) {
				// Store in cache
				setSignedURLs(response.urls);
				
				// Add to result
				Object.assign(result, response.urls);
			}
		} catch (error) {
			console.error('Error fetching signed URLs:', error);
			// Return cached results even if fetch fails
		}
	}

	return result;
};

/**
 * Trigger batch image processing for a project
 * @param {string} projectId - Project ID to process images for
 * @returns {Promise<Object>} - Processing result with counts
 */
export const processImages = async (projectId) => {
	if (!projectId) {
		throw new Error('projectId is required');
	}

	console.log('🔄 Starting batch image processing for project:', projectId);
	
	try {
		const response = await callFirebaseFunction('processImages', {
			projectId,
		});

		console.log('✅ Batch processing complete:', response);
		return response;
	} catch (error) {
		console.error('❌ Batch processing failed:', error);
		throw error;
	}
};

// Deprecated - kept for backward compatibility
export const extractToken = (downloadURL) => {
	const parts = downloadURL.split('token=');
	const token = parts[1];
	return token;
};

// Deprecated - use getSignedURLs instead
export const constructURL = (orgId, projectId, projectName, fileName, token) => {
	const baseURL = `https://firebasestorage.googleapis.com/v0/b/${firebaseConfig.storageBucket}/o/`;
	const path = `${orgId}%2F${projectId}%2Fimages%2F${fileName}`;
	const url = `${baseURL}${path}?alt=media&token=${token}`;
	return url;
};